import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import {
  usePersonalDataForm,
  useRegistrationRouteState,
} from '../features/registration/model';
import {
  PersonalDataFormSection,
  PersonalDataSubmitButton,
} from '../features/registration/ui';
import {
  type EmployeeProfile,
  getEmployeeProfile,
} from '../shared/api/employee.service';
import { zIndexMap } from '../shared/config';
import { useEmployeeSession } from '../shared/hooks/use-employee-session';
import {
  resolveEmployeeSessionCountryCode,
  resolveTaxIdFieldConfig,
} from '../shared/model';
import { TaxIdHelpSheet } from '../shared/ui';

const FOOTER_GAP_PX = 14;
const FOOTER_BOTTOM_PADDING_PX = 8;
const FOOTER_SECONDARY_MIN_HEIGHT_PX = 36;

const Screen = styled.div.attrs({
  'data-ui': 'personal-data-page',
})`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;

  & > [data-ui='registration-identity-personal-data-form'] {
    margin-bottom: auto;
  }
`;

const SubmitError = styled.p`
  margin: 0;
  text-align: center;
  font-size: ${({ theme }) => theme.scale.px(12)};
  font-weight: 500;
  line-height: 1.2;
  color: ${({ theme }) => theme.colors.support.danger};
`;

const Footer = styled.div.attrs({
  'data-ui': 'personal-data-page-footer',
})`
  flex-shrink: 0;
  display: grid;
  justify-items: center;
  row-gap: ${({ theme }) => theme.scale.px(FOOTER_GAP_PX)};
  padding-bottom: ${({ theme }) => theme.scale.px(FOOTER_BOTTOM_PADDING_PX)};
`;

const FooterSecondary = styled.div.attrs({
  'data-ui': 'personal-data-page-footer-secondary',
})`
  width: 100%;
  min-height: ${({ theme }) => theme.scale.px(FOOTER_SECONDARY_MIN_HEIGHT_PX)};
  display: grid;
  justify-items: center;
  align-content: start;
`;

function resolvePersonalDataFormKey(
  countryCode: string,
  firstName?: string,
  lastName?: string,
  taxId?: string,
  updatedAt?: string,
): string {
  return JSON.stringify([
    countryCode,
    updatedAt ?? null,
    firstName ?? null,
    lastName ?? null,
    taxId ?? null,
  ]);
}

type PersonalDataPageFormProps = {
  countryCode: string;
  initialProfile: EmployeeProfile | null;
  onOpenTaxIdHelp: () => void;
};

function PersonalDataPageForm({
  countryCode,
  initialProfile,
  onOpenTaxIdHelp,
}: PersonalDataPageFormProps) {
  const navigate = useNavigate();
  const registrationRoute = useRegistrationRouteState();
  const personalDataForm = usePersonalDataForm({
    countryCode,
    initialProfile,
  });

  const handleSubmit = async () => {
    if (!personalDataForm.canSubmit || personalDataForm.isSubmitting) {
      return;
    }

    const isSubmitted = await personalDataForm.submit();
    if (!isSubmitted) {
      return;
    }

    const nextPath = registrationRoute.nextImplementedPath;
    if (!nextPath) {
      return;
    }

    navigate(nextPath);
  };

  return (
    <Screen>
      <PersonalDataFormSection
        isTaxIdInvalid={personalDataForm.isTaxIdInvalid}
        onOpenTaxIdHelp={onOpenTaxIdHelp}
        onFieldChange={personalDataForm.setFieldValue}
        taxIdErrorMessage={personalDataForm.taxIdErrorMessage}
        taxIdField={personalDataForm.taxIdField}
        values={personalDataForm.values}
      />
      <Footer>
        <PersonalDataSubmitButton
          canSubmit={
            personalDataForm.canSubmit && !personalDataForm.isSubmitting
          }
          onSubmit={handleSubmit}
        />
        <FooterSecondary>
          {personalDataForm.submitError ? (
            <SubmitError>{personalDataForm.submitError}</SubmitError>
          ) : null}
        </FooterSecondary>
      </Footer>
    </Screen>
  );
}

export function PersonalDataPage() {
  const [isTaxIdHelpOpen, setIsTaxIdHelpOpen] = useState(false);
  const { authContact, status } = useEmployeeSession();
  const profileQuery = useQuery({
    enabled: status === 'authenticated',
    queryFn: getEmployeeProfile,
    queryKey: ['employee', 'profile'],
  });
  const profile = profileQuery.data ?? null;
  const countryCode = resolveEmployeeSessionCountryCode(profile, authContact);
  const taxIdField = resolveTaxIdFieldConfig(countryCode);

  return (
    <>
      <PersonalDataPageForm
        key={resolvePersonalDataFormKey(
          countryCode,
          profile?.firstName,
          profile?.lastName,
          profile?.tin,
          profile?.updatedAt,
        )}
        countryCode={countryCode}
        initialProfile={profile}
        onOpenTaxIdHelp={() => setIsTaxIdHelpOpen(true)}
      />
      <TaxIdHelpSheet
        dataUi="registration-identity-tax-id-help-sheet"
        isOpen={isTaxIdHelpOpen}
        onClose={() => setIsTaxIdHelpOpen(false)}
        taxIdField={taxIdField}
        zIndex={zIndexMap.registration.taxIdHelpOverlay}
      />
    </>
  );
}
